// src/components/pages/dialogs/ColumnsSelectorDialog.tsx
'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { GripVertical, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';

export type ToggleableColumn = {
  id: string;
  label: string;
  enabled: boolean;
};

interface ColumnsSelectorDialogProps<T extends ToggleableColumn> {
  open: boolean;
  title?: string;
  description?: string;
  columns: T[];
  onChange: (columns: T[]) => void;
  onClose: () => void;
  onReset: () => void;
}

export default function ColumnsSelectorDialog<T extends ToggleableColumn>({
  open,
  title,
  description,
  columns,
  onChange,
  onClose,
  onReset,
}: ColumnsSelectorDialogProps<T>) {
  const { t } = useTranslation();

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {title ?? t('columnsSelectorDialog.title')}
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4 text-sm">
            <Label className="text-gray-700 dark:text-gray-300">
              {description ?? t('columnsSelectorDialog.description')}
            </Label>

            <div className="space-y-3">
              {columns.map((col) => (
                <div
                  key={col.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-white/10 px-3 py-2 bg-white dark:bg-white/5"
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                    <span className="text-sm text-gray-800 dark:text-white">
                      {col.label}
                    </span>
                  </div>

                  <Switch
                    checked={col.enabled}
                    onCheckedChange={(checked) =>
                      onChange(
                        columns.map((c) =>
                          c.id === col.id
                            ? ({ ...c, enabled: checked } as T)
                            : c
                        )
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="destructive"
            onClick={onReset}
            className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
          >
            {t('columnsSelectorDialog.reset')}
          </Button>

          <Button
            variant="outline"
            onClick={onClose}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            {t('columnsSelectorDialog.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}